import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { getUsersMeRetrieveQueryOptions } from "@/api/generated/backendAPI";
import { UserLicensesGetRequest } from "@/api/generated/backendAPI.schemas";
import CompetitionImage from "@/components/ui/CompetitionImage";
import { useSidebarContent } from "@/providers/useSidebar";
import { CompetitionListSidebar } from "@/components/layout/CompetitionListSidebar";
import { navListBaseStyle } from "@/components/layout/competitionNavStyles";
import { getCompetitionStatus } from "@/lib/competitionStatus";

const TabType = ["all", "held", "ended"];
type tabType = (typeof TabType)[number];

const navListStyle = `${navListBaseStyle} cursor-pointer data-[selected='true']:border-subcolor data-[selected='true']:text-darkcolor`;
const cardStyle = "flex items-center justify-between p-4 bg-white rounded-md shadow-slate-500";

export const Route = createFileRoute("/_layout/_auth/competitions/")({
  loader: ({ context: { queryClient } }) => {
    return queryClient.ensureQueryData(getUsersMeRetrieveQueryOptions());
  },
  component: Competition,
});

function Competition() {
  const [tab, setTab] = useState<tabType>("all");
  useSidebarContent(<CompetitionListSidebar />, []);

  const { data: userData } = useSuspenseQuery(getUsersMeRetrieveQueryOptions());
  const heldCompetitions = userData?.licenses.filter((license) => {
    return new Date(license.endDatetime || "") >= new Date();
  });
  const endedCompetitions = userData?.licenses.filter((license) => {
    return new Date(license.endDatetime || "") < new Date();
  });

  const displayCompetitionList = (license: UserLicensesGetRequest) => {
    const status = getCompetitionStatus(license.startDatetime || "", license.endDatetime || "");
    return (
      <li key={license.licenseId} className={cardStyle}>
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className="w-16 h-16">
              <CompetitionImage
                licenseId={license.licenseId}
                imageType="sub"
                showNoImage={true}
                width={64}
                height={64}
                style={{ objectFit: "cover", width: "100%", height: "100%" }}
                className="rounded-md overflow-hidden"
              />
            </div>
          </div>
          <div>
            <h3 className="mb-2">
              <Link
                to="/competitions/$licenseId/info"
                params={{ licenseId: license.licenseId }}
                className="font-bold text-linkcolor"
              >
                {license.licenseName}
              </Link>
            </h3>
            <p className="text-sm text-slate-800">{license.shortDescription}</p>
            <div className="mt-2 text-sm">
              <dl className="flex items-center justify-start gap-2">
                <dt>投稿締切日：</dt>
                <dd className="mr-2">
                  {license.endDatetime ? new Date(license.endDatetime).toLocaleDateString() : "_"}
                </dd>
                <dt>投稿数：</dt>
                <dd className="mr-2">
                  <span className="mr-2">{license.submissionCount}</span>
                  <Link
                    to="/competitions/$licenseId/score"
                    params={{ licenseId: license.licenseId }}
                    className="text-blue-800"
                  >
                    リーダーボード
                  </Link>
                </dd>
              </dl>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center min-w-[15%] flex-shrink-0">
          {status === "ended" ? (
            <button className="text-sm border border-graycolor text-graycolor bg-white rounded-md px-4 py-2">
              終了
            </button>
          ) : status === "upcoming" ? (
            <button className="text-sm border border-graycolor text-graycolor bg-white rounded-md px-4 py-2">
              受付前
            </button>
          ) : (
            <Link to="/competitions/$licenseId/info" params={{ licenseId: license.licenseId }}>
              <button className="text-sm border border-subcolor text-subcolor bg-white rounded-md px-4 py-2">
                受付中
              </button>
            </Link>
          )}
        </div>
      </li>
    );
  };

  const handleSelectTab = (e: React.MouseEvent<HTMLElement>) => {
    const selectedTab = e.currentTarget.id;
    if (TabType.includes(selectedTab)) {
      setTab(e.currentTarget.id);
    }
  };

  return (
    <div className="p-8 w-full">
      <h1 className="text-[24px] font-bold text-headcolor mb-8">コンペティション一覧</h1>
      <div className="mb-4">
        <ul className="flex justify-start">
          <li id="all" data-selected={tab === "all"} className={navListStyle} onClick={handleSelectTab}>
            すべて
          </li>
          <li id="held" data-selected={tab === "held"} className={navListStyle} onClick={handleSelectTab}>
            受付中
          </li>
          <li id="ended" data-selected={tab === "ended"} className={navListStyle} onClick={handleSelectTab}>
            終了
          </li>
        </ul>
      </div>
      <div className="mt-4 bg-white rounded-md shadow-xs p-4">
        <ul className="flex flex-col gap-4">
          {tab === "all" && userData?.licenses.map(displayCompetitionList)}
          {tab === "held" && heldCompetitions?.map(displayCompetitionList)}
          {tab === "ended" && endedCompetitions?.map(displayCompetitionList)}
        </ul>
      </div>
    </div>
  );
}
